import { onQuestionAnswered } from '../actions/actions.answers';
import { isNeedsReviewOffer } from '../actions/actions.offer';
import { actions as optimizelyActions } from '../actions/actions.optimizely';
import { HistoryIcon } from '../interfaces/interface.consumer-market-confidence';
import { AutoCheckData } from '../interfaces/interface.vehicle';
import { fetchAutoCheckData } from '../services/service.autoCheck';
import EventBus from '../services/service.eventBus';
import { state as autoCheckState } from '../store/store.autocheck';
import { state as consumerMarketConfidenceState } from '../store/store.consumer-market-confidence';
import { state as offerState } from '../store/store.offer';
import { state as pageParamsState } from '../store/store.page-params';
import { trackAutoCheckSelection } from '../tracking/tracking.vehicleHistory';
import {
  QUESTION_ARE_QUESTIONS_VERIFIED,
  QUESTION_AUCTION_QUESTION_ANSWERED_BY_USER,
  QUESTION_AUTOCHECK_DATA_JSON,
  QUESTION_HAS_FLOOD_DAMAGE,
  QUESTION_HAS_FRAME_DAMAGE,
  QUESTION_IS_AUTOCHECK_AUCTION_ELIGIBLE,
  QUESTION_IS_SALVAGE,
  QUESTION_NOT_ACTUAL_MILEAGE,
  QUESTION_ORIGINAL_OWNER,
  QUESTION_PREVIOUSLY_CANADIAN,
  QUESTION_VEHICLE_AUCTION,
  QUESTION_VEHICLE_ISSUES,
  QUESTION_VEHICLE_RENTAL,
  QUESTION_VERIFIED_MILEAGE,
} from '../utils/utils.question';

const onAutoCheckFetch = async (vin: string): Promise<void> => {
  EventBus.publish('AutoCheckFetch', vin);
  try {
    onAutoCheckFetchSuccess(await fetchAutoCheckData(vin));
  } catch (error) {
    onAutoCheckFetchFail(error);
  }
};

const onAutoCheckFetchSuccess = (autoCheckData: AutoCheckData) => {
  const {
    autoCheckFullReportLink,
    hasSingleOwner,
    hasPriorRentals,
    reportDate,
    hasAccidents,
    isAutocheckAuctionEligible,
    verifiedMileage,
    isSalvage,
    hasFloodDamage,
    hasFrameDamage,
    previouslyCanadian,
    notActualMileage,
    autoCheckDataJSON,
  } = autoCheckData;
  const offerReview = isNeedsReviewOffer();
  optimizelyActions.onOptimizelyConsumerMarketConfidenceCheck();

  const { isFurtherReviewNeeded, isKelleyBuysYourCar, isInspectionNeeded } = offerState;
  const { isAdmin } = pageParamsState;

  autoCheckState.autoCheckFullReportLink = autoCheckFullReportLink;
  autoCheckState.hasAccidents = hasAccidents;
  autoCheckState.hasPriorRentals = hasPriorRentals;
  autoCheckState.hasSingleOwner = hasSingleOwner;
  autoCheckState.reportDate = reportDate && new Date(reportDate).toLocaleDateString();
  autoCheckState.isAutocheckAuctionEligible = isAutocheckAuctionEligible;
  autoCheckState.verifiedMileage = verifiedMileage;
  autoCheckState.isSalvage = isSalvage;
  autoCheckState.hasFloodDamage = hasFloodDamage;
  autoCheckState.hasFrameDamage = hasFrameDamage;
  autoCheckState.previouslyCanadian = previouslyCanadian;
  autoCheckState.notActualMileage = notActualMileage;
  autoCheckState.autoCheckDataJSON = autoCheckDataJSON;
  autoCheckState.historyIcons = [...getVehicleHistoryIcons()];

  if (offerReview) {
    consumerMarketConfidenceState.shouldDisplayConfidentVehicleHistoryReport =
      !isKelleyBuysYourCar && autoCheckFullReportLink.length > 1 && !isFurtherReviewNeeded && !isInspectionNeeded && !isAdmin;
  } else {
    consumerMarketConfidenceState.shouldDisplayConfidentVehicleHistoryReport = autoCheckFullReportLink.length > 1 && !isAdmin;
  }

  EventBus.publish('AutoCheckFetchSuccess', autoCheckData);
};

const onAutoCheckFetchFail = (error) => {
  const questions = [
    // set answers if autocheck not answering questions
    { question: QUESTION_ARE_QUESTIONS_VERIFIED, answer: 0 },
    { question: QUESTION_VERIFIED_MILEAGE, answer: '' },
    { question: QUESTION_AUCTION_QUESTION_ANSWERED_BY_USER, answer: 1 },
    // Set auction question to false if vehicle activities api is down
    { question: QUESTION_VEHICLE_AUCTION, answer: false },
  ];

  questions.forEach(({ question, answer }) => onQuestionAnswered(question, answer));
  console.error('Error calling autocheck', error);
};

export const getVehicleHistoryIcons = (): HistoryIcon[] => {
  const { hasSingleOwner, hasPriorRentals, hasAccidents } = autoCheckState;
  const { accidentQuestionModifiedByDealer } = offerState;

  const historyIcons: HistoryIcon[] = [];

  // Owner icon is always displayed
  historyIcons.push({ icon: hasSingleOwner ? 'one-owner-history-report' : 'multiple-owners-history-report', label: hasSingleOwner ? 'One Owner' : 'Multiple Owners' });

  // Rental icon is always displayed
  historyIcons.push({ icon: hasPriorRentals ? 'prior-rental-history-report' : 'non-rental-history-report', label: hasPriorRentals ? 'Prior Rental' : 'Non-Rental' });

  // Accident icon is only displayed if dealer didn't edit the question
  if (!accidentQuestionModifiedByDealer) {
    historyIcons.push({ icon: hasAccidents ? 'accidents-found-history-report' : 'no-accidents-history-report', label: hasAccidents ? 'Accident Found' : 'No Accidents' });
  }

  return historyIcons;
};

export const onAutoCheckFullReportClick = (): void => {
  trackAutoCheckSelection();
};

const onOfferLoad = (offer) => {
  const { prospect } = offer;
  const { vehicle } = prospect;

  if (vehicle.vin) {
    onAutoCheckFetch(vehicle.vin);
  }
};

const onAutoCheckInit = async (vin: string): Promise<boolean> => {
  await onAutoCheckFetch(vin);

  if (!autoCheckState.reportDate) {
    return false;
  }

  const questions = [
    { question: QUESTION_ORIGINAL_OWNER, answer: autoCheckState.hasSingleOwner },
    { question: QUESTION_VEHICLE_ISSUES, answer: autoCheckState.hasAccidents ? 'Yes' : 'No' },
    { question: QUESTION_VEHICLE_AUCTION, answer: autoCheckState.hasBeenToAuction },
    { question: QUESTION_VEHICLE_RENTAL, answer: autoCheckState.hasPriorRentals },
    { question: QUESTION_ARE_QUESTIONS_VERIFIED, answer: 1 },
    { question: QUESTION_VERIFIED_MILEAGE, answer: autoCheckState.verifiedMileage },
    { question: QUESTION_AUCTION_QUESTION_ANSWERED_BY_USER, answer: 0 },
    { question: QUESTION_HAS_FLOOD_DAMAGE, answer: autoCheckState.hasFloodDamage },
    { question: QUESTION_HAS_FRAME_DAMAGE, answer: autoCheckState.hasFrameDamage },
    { question: QUESTION_IS_SALVAGE, answer: autoCheckState.isSalvage },
    { question: QUESTION_PREVIOUSLY_CANADIAN, answer: autoCheckState.previouslyCanadian },
    { question: QUESTION_NOT_ACTUAL_MILEAGE, answer: autoCheckState.notActualMileage },
    { question: QUESTION_IS_AUTOCHECK_AUCTION_ELIGIBLE, answer: autoCheckState.isAutocheckAuctionEligible },
    { question: QUESTION_AUTOCHECK_DATA_JSON, answer: autoCheckState.autoCheckDataJSON },
  ];

  questions.forEach(({ question, answer }) => onQuestionAnswered(question, answer));

  return true;
};

const actions = {
  onAutoCheckFetch,
  onAutoCheckFetchSuccess,
  onAutoCheckFetchFail,
  onOfferLoad,
  onAutoCheckInit,
};

export { actions };
export default actions;
